import { useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { cleanAll, connectToHubSessionUser } from "src/redux/slices/hub";

import buildHubUrl from "./buildHubUrl";
import CreateConnection from "./HubMatrix";
import useAuth from "src/hooks/useAuth";
import { useHistory } from "react-router-dom";
import useIsMountedRef from "src/hooks/useIsMountedRef";
import { refreshSessions } from "src/redux/slices/authJwt";
import { clearLocalStoragePreservingQr } from "src/utils/tmoQrStorage";
import { onSessionIdentifierChange, readStoredSessionIdentifier } from "src/utils/deviceSessionCore";
import { DEVICE_SESSION_CLOSED_EVENT } from "src/utils/deviceSession";

export const DEVICE_SESSION_CLOSED_MESSAGE =
  "La sesión de este dispositivo se cerró desde otro dispositivo (o por el límite de sesiones). Vuelve a iniciar sesión.";

export default function useUserSessionHub() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authJwt);
  const { isAuthenticated } = useAuth();
  const isMountedRef = useIsMountedRef();
  const history = useHistory();
  const { logout } = useAuth();
  // Guardia local — antes leía `state.hub.hubSessionUser.connectedHub`
  // (Redux), un flag que se pone a true al arrancar y NUNCA se resetea
  // cuando HubConnectionManager cierra el websocket. Tras un remount con
  // la conexión ya muerta, el efecto se saltaba y `hub/usersession` no
  // volvía a abrir. El bug se enmascaraba porque otro consumidor mantiene
  // refCount=2 — pero si se quedaba a 0, no había vuelta sin recargar.
  const isConnectedRef = useRef(false);
  const { enqueueSnackbar } = useSnackbar();

  // 2026-10-26 · El evento de cierre remoto lleva el identificador en el
  // nombre: antes se leía UNA vez al montar. Si el TPV montó sin identificador
  // (o lo recuperó después) escuchaba `.../session/null` y nunca se enteraba
  // de que su sesión se había cerrado: seguía operando con un JWT válido y el
  // lease de mesa fallaba (400 DEVICE_REQUIRED). Ahora se re-suscribe al cambiar.
  const [sid, setSid] = useState(() => readStoredSessionIdentifier());
  useEffect(() => onSessionIdentifierChange((next) => setSid(next)), []);

  const closeThisSessionRef = useRef(null);
  closeThisSessionRef.current = async ({ notice = false } = {}) => {
    await logout();
    dispatch(cleanAll());
    clearLocalStoragePreservingQr();
    if (notice) {
      enqueueSnackbar(DEVICE_SESSION_CLOSED_MESSAGE, { variant: "warning", preventDuplicate: true, autoHideDuration: 10000 });
    }
    if (isMountedRef.current) {
      history.push("/");
    }
  };

  // El API confirmó al arrancar que este dispositivo ya no tiene sesión
  // (utils/deviceSession.verify): al login con el motivo, sin quedarse
  // «medio logueado».
  useEffect(() => {
    if (!user?.id || typeof window === "undefined") return undefined;
    const onClosed = () => closeThisSessionRef.current?.({ notice: true });
    window.addEventListener(DEVICE_SESSION_CLOSED_EVENT, onClosed);
    return () => window.removeEventListener(DEVICE_SESSION_CLOSED_EVENT, onClosed);
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || isConnectedRef.current) return;

    isConnectedRef.current = true;

    const cleanup = CreateConnection(
      buildHubUrl("/hub/usersession", { restauranteId: user.id }),
      [
        {
          id: `user/${user.id}/session/${sid}`,
          operator: async () => {
            await closeThisSessionRef.current?.({ notice: true });
          },
        },
        {
          id: `user/${user.id}`,
          operator: async () => {
            await logout();
            dispatch(cleanAll());
            clearLocalStoragePreservingQr();

            if (isMountedRef.current) {
              history.push("/");
            }
          },
        },
        {
          id: `user/sync/${user.id}`,
          operator: async () => {
            // sync user sessions
            if (isAuthenticated) dispatch(refreshSessions());
          },
        },
        {
          id: `user/sync/only/${user.id}`,
          operator: async (sessionId) => {
            if (isAuthenticated) dispatch(refreshSessions());
          },
        },
      ],
      dispatch,
      connectToHubSessionUser,
      "userSession"
    );

    return () => {
      // Antes no había cleanup → fuga de refCount al manager. Cada
      // remount sumaba +1 sin restar, y la conexión nunca se cerraba
      // ni cuando el último consumidor real se iba.
      cleanup();
      isConnectedRef.current = false;
    };
  }, [user?.id, sid]);
}
